<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { Doughnut } from 'vue-chartjs';
    import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
    import { applyChartDefaults, generateColors } from '@shared/modules/chartjs-defaults';
    import RangeSliderNative from '@shared/components/RangeSliderNative.vue';
    import Loading from '@shared/components/Loading.vue';
    import type { BackOfficeDashboardAccess } from './BackOfficeDashboard.access';

    ChartJS.register(ArcElement, Tooltip, Legend);
    applyChartDefaults();

    const props = defineProps<{
        dashboardAccess: BackOfficeDashboardAccess;
    }>();

    const router = useRouter();

    const industryCountRange = computed(() => {
        const counts = (props.dashboardAccess.state.industryCounts ?? []).filter((i) => i.IndustryId !== null).map((i) => i.Count);
        return { min: counts.length ? Math.min(...counts) : 0, max: counts.length ? Math.max(...counts) : 100 };
    });

    const showRangeSliders = ref(false);
    const minCount = ref(10);
    const maxCount = ref(industryCountRange.value.max);

    watch(industryCountRange, (range) => {
        maxCount.value = range.max;
    });

    const filteredIndustryCounts = computed(() =>
        (props.dashboardAccess.state.industryCounts ?? []).filter((i) => i.IndustryId !== null && i.Count >= minCount.value && i.Count <= maxCount.value)
    );

    const industryChartData = computed(() => {
        const counts = filteredIndustryCounts.value;
        return {
            labels: counts.map((i) => i.Name),
            datasets: [
                {
                    data: counts.map((i) => i.Count),
                    backgroundColor: generateColors(counts.length),
                },
            ],
        };
    });

    const industryChartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' as const },
        },
        onClick: (_event: unknown, elements: { index: number }[]) => {
            if (!elements.length) return;
            const industry = filteredIndustryCounts.value[elements[0].index];
            if (industry?.IndustryId) {
                props.dashboardAccess.getCompaniesByIndustry(industry.IndustryId, industry.Name);
            }
        },
    }));
</script>

<template>
    <div>
        <h2 class="h4">Customers per industry</h2>
        <section class="ug-panel print-block">
            <div class="panel-body">
                <div style="width: 100%; max-height: 400px">
                    <Doughnut :data="industryChartData" :options="industryChartOptions" />
                </div>

                <div class="form-group m-xs-t m-sm-b text-center" v-show="!showRangeSliders">
                    <button class="btn btn-xs btn-default" @click="showRangeSliders = true">
                        {{ 'Show filters' }}
                    </button>
                </div>

                <div v-if="showRangeSliders" class="row m-sm-b">
                    <div class="col-sm-6">
                        <RangeSliderNative v-model="minCount" label="Min count" :min="industryCountRange.min" :max="maxCount" />
                    </div>
                    <div class="col-sm-6">
                        <RangeSliderNative v-model="maxCount" label="Max count" :min="minCount" :max="industryCountRange.max" />
                    </div>
                </div>

                <div v-if="dashboardAccess.state.selectedIndustryName">
                    <h3 class="h5">{{ dashboardAccess.state.selectedIndustryName }}</h3>
                    <ul class="list-unstyled list-border-divider list-item-state-hover">
                        <li v-for="company in dashboardAccess.state.industryCompanies" :key="company.ServiceSubscriptionId">
                            <a
                                class="d-block p-sm-ver p-lg-hor"
                                href="#"
                                @click.prevent="router.push({ name: 'SubscriptionEdit', params: { serviceSubscriptionId: company.ServiceSubscriptionId } })">
                                {{ company.Name }}
                            </a>
                        </li>
                    </ul>
                    <Loading :show="dashboardAccess.state.loadingCompanies" />
                </div>
            </div>
        </section>
    </div>
</template>
